import React, { useEffect, useRef, useState } from "react";
import Shell from "../components/Shell";
import { api, fmtMoney } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Target, Trash2, Calendar as CalIcon, ExternalLink, Pencil } from "lucide-react";
import { toast } from "sonner";
import confetti from "canvas-confetti";

const CELEBRATED_KEY = "argent-goals-celebrated";

// Spider-web themed confetti burst: red, navy, gold — plus a couple of
// staggered follow-up pops so it feels like a proper "you did it" moment.
function fireGoalConfetti() {
  const colors = ["#DC1F26", "#0F172A", "#F59E0B", "#F8FAFC"];
  const shoot = (originX, delay = 0) => setTimeout(() => {
    confetti({
      particleCount: 90,
      spread: 70,
      startVelocity: 55,
      origin: { x: originX, y: 0.7 },
      colors,
      scalar: 1.1,
      ticks: 220,
    });
  }, delay);
  shoot(0.2, 0);
  shoot(0.8, 180);
  shoot(0.5, 360);
}


export default function Goals() {
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [saved, setSaved] = useState("0");
  const [deadline, setDeadline] = useState("");
  const [sync, setSync] = useState(true);

  const cur = user?.currency || "USD";
  // Track which goals already celebrated so refreshing doesn't re-fire the
  // burst every time you visit the page.
  const celebratedRef = useRef(new Set(
    (() => { try { return JSON.parse(localStorage.getItem(CELEBRATED_KEY) || "[]"); } catch { return []; } })()
  ));

  const load = async () => {
    const r = await api.get("/goals");
    setGoals(r.data);
    // Fire confetti for any goal that just crossed 100% and hasn't been
    // celebrated yet. Persist so we only celebrate each goal once.
    const newly = (r.data || []).filter(g => g.target_amount > 0 && g.saved_amount >= g.target_amount && !celebratedRef.current.has(g.id));
    if (newly.length) {
      fireGoalConfetti();
      newly.forEach(g => {
        celebratedRef.current.add(g.id);
        toast.success(`Goal complete: ${g.name}`, { duration: 4200 });
      });
      try { localStorage.setItem(CELEBRATED_KEY, JSON.stringify([...celebratedRef.current])); } catch {}
    }
    // If a goal drops back below target (user edited target up), allow
    // re-celebration by removing it from the set.
    (r.data || []).forEach(g => {
      if (celebratedRef.current.has(g.id) && g.saved_amount < g.target_amount) {
        celebratedRef.current.delete(g.id);
      }
    });
    try { localStorage.setItem(CELEBRATED_KEY, JSON.stringify([...celebratedRef.current])); } catch {}
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null); setName(""); setTarget(""); setSaved("0"); setDeadline(""); setSync(true); setOpen(true);
  };
  const openEdit = (g) => {
    setEditing(g); setName(g.name); setTarget(String(g.target_amount)); setSaved(String(g.saved_amount));
    setDeadline(g.deadline?.slice(0, 10) || ""); setSync(!!g.calendar_url); setOpen(true);
  };

  const save = async () => {
    if (!name || !target || !deadline) { toast.error("Name, target and deadline are required"); return; }
    const payload = { name, target_amount: parseFloat(target), saved_amount: parseFloat(saved || "0"), deadline: new Date(deadline).toISOString(), sync_calendar: sync };
    try {
      if (editing) { await api.put(`/goals/${editing.id}`, payload); toast.success("Goal updated"); }
      else { await api.post("/goals", payload); toast.success("Goal created"); }
      setOpen(false); load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => { await api.delete(`/goals/${id}`); toast.success("Deleted"); load(); };

  return (
    <Shell>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-display text-4xl font-bold text-slate-900 dark:text-slate-100">Savings Goals</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Set targets. Watch progress. Celebrate.</p>
        </div>
        <Button onClick={openNew} className="rounded-full bg-spidey-red text-white hover:opacity-90" data-testid="add-goal-btn">
          <Plus className="w-4 h-4 mr-1" /> New Goal
        </Button>
      </div>

      {goals.length === 0 ? (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-16 text-center">
          <Target className="w-8 h-8 text-slate-300 mx-auto" />
          <div className="mt-4 font-display text-xl font-semibold text-slate-900 dark:text-slate-100">No goals yet</div>
          <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">Start with your first savings goal</div>
          <Button onClick={openNew} className="mt-6 rounded-full bg-spidey-red text-white hover:opacity-90" data-testid="empty-goal-btn">
            <Plus className="w-4 h-4 mr-1" /> Create goal
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
          {goals.map(g => {
            const pct = g.target_amount > 0 ? Math.min(100, Math.round((g.saved_amount / g.target_amount) * 100)) : 0;
            const dl = new Date(g.deadline);
            const days = Math.ceil((dl - new Date()) / (1000 * 60 * 60 * 24));
            return (
              <div key={g.id} data-testid={`goal-card-${g.id}`} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 hover:-translate-y-0.5 transform transition-transform duration-200">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">Goal</div>
                    <div className="font-display text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1">{g.name}</div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(g)} className="w-8 h-8 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 dark:bg-slate-800 grid place-items-center" data-testid={`edit-goal-${g.id}`}>
                      <Pencil className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                    </button>
                    <button onClick={() => remove(g.id)} className="w-8 h-8 rounded-full hover:bg-rose-50 grid place-items-center" data-testid={`delete-goal-${g.id}`}>
                      <Trash2 className="w-4 h-4 text-rose-500" />
                    </button>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="flex justify-between text-sm mb-2">
                    <div className="tabular font-semibold text-slate-900 dark:text-slate-100">{fmtMoney(g.saved_amount, cur)}</div>
                    <div className="tabular text-slate-500 dark:text-slate-400">{fmtMoney(g.target_amount, cur)}</div>
                  </div>
                  <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }}></div>
                  </div>
                  <div className="flex justify-between text-xs mt-2">
                    <div className="font-semibold text-emerald-700">{pct}% saved</div>
                    <div className="text-slate-500 dark:text-slate-400">{days > 0 ? `${days} days left` : "Deadline passed"}</div>
                  </div>
                </div>

                {g.calendar_url && (
                  <a href={g.calendar_url} target="_blank" rel="noreferrer" data-testid={`goal-cal-${g.id}`} className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold spidey-red hover:underline">
                    <CalIcon className="w-3.5 h-3.5" /> Add deadline to Google Calendar <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="goal-dialog" className="rounded-3xl bg-white dark:bg-slate-900 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-bold">{editing ? "Edit Goal" : "New Goal"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">Name</Label>
              <Input data-testid="goal-name-input" value={name} onChange={e => setName(e.target.value)} className="rounded-xl mt-1.5" placeholder="e.g. Tokyo trip" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">Target</Label>
                <Input data-testid="goal-target-input" type="number" step="0.01" value={target} onChange={e => setTarget(e.target.value)} className="rounded-xl mt-1.5" />
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">Saved</Label>
                <Input data-testid="goal-saved-input" type="number" step="0.01" value={saved} onChange={e => setSaved(e.target.value)} className="rounded-xl mt-1.5" />
              </div>
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">Deadline</Label>
              <Input data-testid="goal-deadline-input" type="date" value={deadline} onChange={e => setDeadline(e.target.value)} className="rounded-xl mt-1.5" />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-slate-200 dark:border-slate-800 px-4 py-3">
              <div>
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">Sync to Google Calendar</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Get a reminder on your deadline</div>
              </div>
              <Switch data-testid="goal-sync-switch" checked={sync} onCheckedChange={setSync} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full" data-testid="goal-cancel-btn">Cancel</Button>
            <Button onClick={save} className="rounded-full bg-spidey-red text-white hover:opacity-90" data-testid="goal-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
